import React, { useState, useEffect, useCallback } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

/* ─── helpers ────────────────────────────────────────────────────────────── */
const getYouTubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
};
const getVideoThumb = (v) => {
    const ytId = getYouTubeId(v.youtube_link || v.video_path);
    return ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg` : null;
};
const resolveVideoUrl = (v) => {
    const ytId = getYouTubeId(v.youtube_link) || getYouTubeId(v.video_path);
    if (ytId) return { type: 'youtube', ytId };
    const src = v.video_path || v.input_video;
    if (src) return { type: 'local', src: src.startsWith('http') ? src : `${API_LINK}${src.startsWith('/') ? '' : '/'}${src}` };
    return null;
};
const resolveNoteUrl = (n) => {
    const p = n.file_path || n.notes_pdf;
    if (!p) return null;
    return p.startsWith('http') ? p : `${API_LINK}${p.startsWith('/') ? '' : '/'}${p}`;
};

/* ─── VideoPlayer ─────────────────────────────────────────────────────────── */
const VideoPlayer = ({ video }) => {
    const info = resolveVideoUrl(video);
    if (!info) return (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#888', background: '#f5f5f5', borderRadius: '12px' }}>🚫 No media available for this video.</div>
    );
    if (info.type === 'youtube') return (
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
            <iframe
                src={`https://www.youtube.com/embed/${info.ytId}?rel=0`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            />
        </div>
    );
    return (
        <video controls style={{ width: '100%', maxHeight: '420px', display: 'block', background: '#000' }} src={info.src}>
            Your browser does not support video playback.
        </video>
    );
};

/* ─── Toast ───────────────────────────────────────────────────────────────── */
const Toast = ({ msg, type }) => (
    <div className={`tel-toast tel-toast-${type}`}>{msg}</div>
);

/* ─── Modal ───────────────────────────────────────────────────────────────── */
const Modal = ({ title, onClose, children }) => (
    <div className="tel-overlay" onClick={onClose}>
        <div className="tel-modal" onClick={e => e.stopPropagation()}>
            <div className="tel-modal-header">
                <h3 className="tel-modal-title">{title}</h3>
                <button className="tel-modal-close" onClick={onClose}>✕</button>
            </div>
            {children}
        </div>
    </div>
);

/* ─── Main Component ──────────────────────────────────────────────────────── */
const TeacherElearning = ({ onNavigate, userInfo }) => {
    const [playlists,    setPlaylists]    = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [search,       setSearch]       = useState('');
    const [selPlaylist,  setSelPlaylist]  = useState(null);
    const [detail,       setDetail]       = useState(null);
    const [detailLoad,   setDetailLoad]   = useState(false);
    const [toast,        setToast]        = useState(null);
    const [isMobile,     setIsMobile]     = useState(window.innerWidth <= 1024);
    const [playingVideo, setPlayingVideo] = useState(null);
    const [activeTab,    setActiveTab]    = useState('videos');

    // Upload modal state
    const [videoModal, setVideoModal] = useState(false);
    const [noteModal,  setNoteModal]  = useState(false);
    const [videoForm,  setVideoForm]  = useState({ title: '', description: '', file: null });
    const [noteForm,   setNoteForm]   = useState({ title: '', file: null });
    const [uploading,  setUploading]  = useState(false);

    // YouTube link modal
    const [ytModal, setYtModal] = useState(false);
    const [ytForm,  setYtForm]  = useState({ title: '', description: '', url: '' });
    const [ytSaving, setYtSaving] = useState(false);

    const token   = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    /* ── fetch lists ── */
    const fetchPlaylists = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_LINK}/api/elearning/teacher/my-playlists`, { headers });
            const r = await res.json();
            if (r.success) setPlaylists(r.data || []);
            else showToast(r.message || 'Failed to load playlists', 'error');
        } catch { showToast('Network error', 'error'); }
        finally { setLoading(false); }
    }, []);

    const fetchDetail = async (id) => {
        try {
            setDetailLoad(true);
            setDetail(null);
            setPlayingVideo(null);
            setActiveTab('videos');
            const res = await fetch(`${API_LINK}/api/elearning/teacher/my-playlists/${id}`, { headers });
            const r = await res.json();
            if (r.success) {
                setDetail(r.data);
                if (r.data?.videos?.length) setPlayingVideo(r.data.videos[0]);
            } else showToast('Failed to load content', 'error');
        } catch { showToast('Network error', 'error'); }
        finally { setDetailLoad(false); }
    };

    useEffect(() => { fetchPlaylists(); }, [fetchPlaylists]);

    /* ── upload video file ── */
    const handleVideoUpload = async () => {
        if (!videoForm.title.trim() || !videoForm.file) return showToast('Title and video file are required', 'error');
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('video', videoForm.file);
            fd.append('playlist_id', selPlaylist.id);
            fd.append('title', videoForm.title);
            fd.append('description', videoForm.description);
            const res = await fetch(`${API_LINK}/api/elearning/teacher/upload`, { method: 'POST', headers, body: fd });
            const r = await res.json();
            if (r.success) {
                showToast('Video uploaded successfully! ✅');
                setVideoModal(false);
                setVideoForm({ title: '', description: '', file: null });
                fetchDetail(selPlaylist.id);
                fetchPlaylists();
            } else showToast(r.message || 'Upload failed', 'error');
        } catch { showToast('Upload error', 'error'); }
        finally { setUploading(false); }
    };

    /* ── add youtube link ── */
    const handleYtSave = async () => {
        if (!ytForm.title.trim() || !ytForm.url.trim()) return showToast('Title and YouTube URL are required', 'error');
        if (!getYouTubeId(ytForm.url)) return showToast('Invalid YouTube URL', 'error');
        setYtSaving(true);
        try {
            const res = await fetch(`${API_LINK}/api/elearning/teacher/youtube`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ playlist_id: selPlaylist.id, title: ytForm.title, description: ytForm.description, youtube_link: ytForm.url })
            });
            const r = await res.json();
            if (r.success) {
                showToast('YouTube link added! ✅');
                setYtModal(false);
                setYtForm({ title: '', description: '', url: '' });
                fetchDetail(selPlaylist.id);
                fetchPlaylists();
            } else showToast(r.message || 'Failed', 'error');
        } catch { showToast('Error', 'error'); }
        finally { setYtSaving(false); }
    };

    /* ── upload note ── */
    const handleNoteUpload = async () => {
        if (!noteForm.title.trim() || !noteForm.file) return showToast('Title and file are required', 'error');
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('note', noteForm.file);
            fd.append('playlist_id', selPlaylist.id);
            fd.append('title', noteForm.title);
            const res = await fetch(`${API_LINK}/api/elearning/teacher/upload-note`, { method: 'POST', headers, body: fd });
            const r = await res.json();
            if (r.success) {
                showToast('Note uploaded! ✅');
                setNoteModal(false);
                setNoteForm({ title: '', file: null });
                fetchDetail(selPlaylist.id);
                fetchPlaylists();
            } else showToast(r.message || 'Failed', 'error');
        } catch { showToast('Upload error', 'error'); }
        finally { setUploading(false); }
    };

    /* ── delete ── */
    const deleteVideo = async (id) => {
        if (!window.confirm('Delete this video permanently?')) return;
        try {
            const res = await fetch(`${API_LINK}/api/teacher/elearning/videos/${id}`, { method: 'DELETE', headers });
            const r = await res.json();
            if (r.success) { showToast('Video deleted'); fetchDetail(selPlaylist.id); fetchPlaylists(); }
            else showToast(r.message || 'Delete failed', 'error');
        } catch { showToast('Error deleting', 'error'); }
    };
    const deleteNote = async (id) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            const res = await fetch(`${API_LINK}/api/teacher/elearning/notes/${id}`, { method: 'DELETE', headers });
            const r = await res.json();
            if (r.success) { showToast('Note deleted'); fetchDetail(selPlaylist.id); fetchPlaylists(); }
            else showToast(r.message || 'Delete failed', 'error');
        } catch { showToast('Error deleting', 'error'); }
    };

    const filtered = playlists.filter(p =>
        `${p.name} ${p.subject} ${p.department}`.toLowerCase().includes(search.toLowerCase())
    );

    const groupedPlaylists = filtered.reduce((acc, p) => {
        const dep = p.department || 'General';
        if (!acc[dep]) acc[dep] = [];
        acc[dep].push(p);
        return acc;
    }, {});

    return (
        <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-elearning" userInfo={userInfo}>
            <style>{CSS}</style>

            {toast && <Toast msg={toast.msg} type={toast.type} />}

            {selPlaylist ? (
                /* ════ PLAYLIST DETAIL VIEW ════ */
                <div>
                    <button className="tel-back" onClick={() => { setSelPlaylist(null); setDetail(null); }}>
                        ← Back to Playlists
                    </button>

                    {/* Playlist header */}
                    <div className="tel-subject-banner">
                        <div>
                            <h2 className="tel-subject-title">{selPlaylist.name}</h2>
                            <div className="tel-subject-meta">
                                <span>🏛️ {selPlaylist.department}</span>
                                <span>📖 {selPlaylist.subject}</span>
                            </div>
                        </div>
                        <div className="tel-action-btns">
                            <button className="tel-btn tel-btn-yt"   onClick={() => setYtModal(true)}>  🔗 Add YouTube</button>
                            <button className="tel-btn tel-btn-green" onClick={() => setVideoModal(true)}>🎥 Upload Video</button>
                            <button className="tel-btn tel-btn-blue"  onClick={() => setNoteModal(true)}> 📝 Upload Note</button>
                        </div>
                    </div>

                    {detailLoad ? (
                        <div className="tel-spinner-wrap"><div className="tel-spinner" />Loading…</div>
                    ) : detail ? (
                        <div className="tel-detail-grid">
                            {/* Video Player */}
                            <div className="tel-section">
                                {playingVideo ? (
                                    <>
                                        <div className="tel-player-card">
                                            <VideoPlayer video={playingVideo} />
                                            <div className="tel-player-info">
                                                <div className="tel-player-title">{playingVideo.title}</div>
                                                {playingVideo.description && <div className="tel-player-desc">{playingVideo.description}</div>}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="tel-player-placeholder">🎬 Select a video below to start watching</div>
                                )}

                                {/* Tabs */}
                                <div className="tel-tabs">
                                    <button className={`tel-tab ${activeTab === 'videos' ? 'tel-tab-active' : ''}`} onClick={() => setActiveTab('videos')}>
                                        🎥 Videos <span className="tel-count">{detail.videos?.length || 0}</span>
                                    </button>
                                    <button className={`tel-tab ${activeTab === 'notes' ? 'tel-tab-active' : ''}`} onClick={() => setActiveTab('notes')}>
                                        📝 Notes <span className="tel-count">{detail.notes?.length || 0}</span>
                                    </button>
                                </div>

                                {/* Video list */}
                                {activeTab === 'videos' && (
                                    <div className="tel-video-list">
                                        {(detail.videos || []).length === 0
                                            ? <div className="tel-empty">No videos yet — use the buttons above to add some!</div>
                                            : (detail.videos || []).map((v, i) => {
                                                const thumb = getVideoThumb(v);
                                                const isPlaying = playingVideo?.id === v.id;
                                                return (
                                                    <div key={v.id}
                                                        className={`tel-video-row tel-vitem ${isPlaying ? 'tel-vitem-active' : ''}`}
                                                        onClick={() => setPlayingVideo(v)}
                                                        style={{ cursor: 'pointer' }}>
                                                        {thumb
                                                            ? <img src={thumb} className="tel-thumb" alt={v.title} />
                                                            : <div className="tel-thumb-num">{i + 1}</div>
                                                        }
                                                        <div className="tel-vinfo">
                                                            <div className="tel-vtitle">{v.title}</div>
                                                            {v.description && <div className="tel-vdesc">{v.description}</div>}
                                                            <div className="tel-vdate">{new Date(v.created_at).toLocaleDateString()}</div>
                                                        </div>
                                                        <div className="tel-row-actions">
                                                            <span className="tel-vitem-icon">{isPlaying ? '🔊' : '▶'}</span>
                                                            <button onClick={(e) => { e.stopPropagation(); deleteVideo(v.id); }} className="tel-icon-btn tel-icon-del">🗑</button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}

                                {/* Notes list */}
                                {activeTab === 'notes' && (
                                    <div className="tel-notes-list">
                                        {(detail.notes || []).length === 0
                                            ? <div className="tel-empty">No notes yet — upload class materials for students!</div>
                                            : (detail.notes || []).map(n => {
                                                const url = resolveNoteUrl(n);
                                                return (
                                                    <div key={n.id} className="tel-note-row">
                                                        <span className="tel-note-icon">📄</span>
                                                        <div className="tel-vinfo">
                                                            <div className="tel-vtitle">{n.title || n.file_name}</div>
                                                            <div className="tel-vdate">{new Date(n.created_at).toLocaleDateString()}</div>
                                                        </div>
                                                        <div className="tel-row-actions">
                                                            {url && <a href={url} target="_blank" rel="noreferrer" className="tel-icon-btn tel-icon-dl">⬇</a>}
                                                            <button onClick={() => deleteNote(n.id)} className="tel-icon-btn tel-icon-del">🗑</button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {/* Upload Video Modal */}
                    {videoModal && (
                        <Modal title="🎥 Upload Video File" onClose={() => setVideoModal(false)}>
                            <div className="tel-form">
                                <label className="tel-label">Title *</label>
                                <input className="tel-input" placeholder="e.g. Module 1 - Introduction" value={videoForm.title} onChange={e => setVideoForm(f => ({ ...f, title: e.target.value }))} />
                                <label className="tel-label">Description</label>
                                <textarea className="tel-input tel-textarea" rows={2} placeholder="Brief description (optional)" value={videoForm.description} onChange={e => setVideoForm(f => ({ ...f, description: e.target.value }))} />
                                <label className="tel-label">Video File * (MP4, WebM — max 500 MB)</label>
                                <input type="file" accept="video/*" className="tel-input" onChange={e => setVideoForm(f => ({ ...f, file: e.target.files[0] }))} />
                                {videoForm.file && <div className="tel-file-name">📎 {videoForm.file.name} ({(videoForm.file.size / 1024 / 1024).toFixed(1)} MB)</div>}
                                <div className="tel-modal-footer">
                                    <button className="tel-btn tel-btn-outline" onClick={() => setVideoModal(false)}>Cancel</button>
                                    <button className="tel-btn tel-btn-green" onClick={handleVideoUpload} disabled={uploading}>{uploading ? '⏳ Uploading…' : '🎥 Upload Video'}</button>
                                </div>
                            </div>
                        </Modal>
                    )}

                    {/* YouTube Link Modal */}
                    {ytModal && (
                        <Modal title="🔗 Add YouTube Video" onClose={() => setYtModal(false)}>
                            <div className="tel-form">
                                <label className="tel-label">Title *</label>
                                <input className="tel-input" placeholder="e.g. Lecture 1 - Basics" value={ytForm.title} onChange={e => setYtForm(f => ({ ...f, title: e.target.value }))} />
                                <label className="tel-label">YouTube URL *</label>
                                <input className="tel-input" placeholder="https://youtu.be/..." value={ytForm.url} onChange={e => setYtForm(f => ({ ...f, url: e.target.value }))} />
                                {ytForm.url && getYouTubeId(ytForm.url) && (
                                    <img src={`https://img.youtube.com/vi/${getYouTubeId(ytForm.url)}/mqdefault.jpg`} alt="preview" className="tel-yt-preview" />
                                )}
                                <label className="tel-label">Description</label>
                                <textarea className="tel-input tel-textarea" rows={2} value={ytForm.description} onChange={e => setYtForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" />
                                <div className="tel-modal-footer">
                                    <button className="tel-btn tel-btn-outline" onClick={() => setYtModal(false)}>Cancel</button>
                                    <button className="tel-btn tel-btn-yt" onClick={handleYtSave} disabled={ytSaving}>{ytSaving ? '⏳ Saving…' : '🔗 Add Link'}</button>
                                </div>
                            </div>
                        </Modal>
                    )}

                    {/* Upload Note Modal */}
                    {noteModal && (
                        <Modal title="📝 Upload Note / Material" onClose={() => setNoteModal(false)}>
                            <div className="tel-form">
                                <label className="tel-label">Title *</label>
                                <input className="tel-input" placeholder="e.g. Module 1 Notes" value={noteForm.title} onChange={e => setNoteForm(f => ({ ...f, title: e.target.value }))} />
                                <label className="tel-label">File * (PDF, DOCX, PPT, etc.)</label>
                                <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.xls,.xlsx" className="tel-input" onChange={e => setNoteForm(f => ({ ...f, file: e.target.files[0] }))} />
                                {noteForm.file && <div className="tel-file-name">📎 {noteForm.file.name}</div>}
                                <div className="tel-modal-footer">
                                    <button className="tel-btn tel-btn-outline" onClick={() => setNoteModal(false)}>Cancel</button>
                                    <button className="tel-btn tel-btn-blue" onClick={handleNoteUpload} disabled={uploading}>{uploading ? '⏳ Uploading…' : '📝 Upload Note'}</button>
                                </div>
                            </div>
                        </Modal>
                    )}
                </div>
            ) : (
                /* ════ PLAYLIST LIST ════ */
                <div>
                    {/* Header */}
                    <div className="tel-hero">
                        <div>
                            <h1 className="tel-hero-title">📚 My E-Learning Playlists</h1>
                            <p className="tel-hero-sub">Upload videos and notes for your students</p>
                        </div>
                        <div className="tel-hero-stats">
                            <div className="tel-stat"><span className="tel-stat-num">{playlists.length}</span><span className="tel-stat-label">Subjects</span></div>
                            <div className="tel-stat"><span className="tel-stat-num">{playlists.reduce((s,p)=>s+(p.video_count||0),0)}</span><span className="tel-stat-label">Videos</span></div>
                            {!isMobile && <div className="tel-stat"><span className="tel-stat-num">{playlists.reduce((s,p)=>s+(p.note_count||0),0)}</span><span className="tel-stat-label">Notes</span></div>}
                        </div>
                    </div>

                    {/* Search */}
                    <div className="tel-search-wrap">
                        <span className="tel-search-icon">🔍</span>
                        <input className="tel-search" placeholder="Search playlists…" value={search} onChange={e => setSearch(e.target.value)} />
                        {search && <button className="tel-clear" onClick={() => setSearch('')}>✕</button>}
                    </div>

                    {loading ? (
                        <div className="tel-spinner-wrap"><div className="tel-spinner" />Loading playlists…</div>
                    ) : filtered.length === 0 ? (
                        <div className="tel-empty-state">
                            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
                            <h3>{search ? 'No results found' : 'No Playlists Found'}</h3>
                            <p>{search ? `No playlists match "${search}"` : 'Contact your admin to get playlists assigned.'}</p>
                        </div>
                    ) : (
                        <div className="tel-departments">
                            {Object.entries(groupedPlaylists)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([dep, list]) => (
                                <div key={dep} className="tel-dep-section">
                                    <div className="tel-dep-header">
                                        <span className="tel-dep-icon">🏛️</span>
                                        <h2 className="tel-dep-title">{dep}</h2>
                                        <span className="tel-dep-count">{list.length} Subjects</span>
                                    </div>
                                    <div className="tel-grid">
                                        {list.map(p => (
                                            <div key={p.id} className="tel-card" onClick={() => { setSelPlaylist(p); fetchDetail(p.id); }}>
                                                <div className="tel-card-stripe" />
                                                <div className="tel-card-body">
                                                    <h3 className="tel-card-title">{p.name}</h3>
                                                    <div className="tel-card-tags">
                                                        {p.subject && p.subject !== p.name && <span className="tel-tag tel-tag-blue">{p.subject}</span>}
                                                    </div>
                                                    <div className="tel-card-counts">
                                                        <span className={p.video_count ? 'tel-count-green' : 'tel-count-grey'}>🎥 {p.video_count || 0} videos</span>
                                                        <span className={p.note_count  ? 'tel-count-blue'  : 'tel-count-grey'}>📝 {p.note_count  || 0} notes</span>
                                                    </div>
                                                </div>
                                                <div className="tel-card-cta">Manage →</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Layout>
    );
};

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

/* Toast */
.tel-toast { position:fixed; top:1.5rem; right:1.5rem; z-index:99999; padding:0.9rem 1.4rem; border-radius:10px; font-size:0.9rem; font-weight:600; box-shadow:0 4px 20px rgba(0,0,0,0.15); animation:telSlide 0.3s ease; }
.tel-toast-success { background:#238548; color:#fff; }
.tel-toast-error   { background:#f44336; color:#fff; }
@keyframes telSlide { from { transform:translateX(120%); opacity:0; } to { transform:translateX(0); opacity:1; } }

/* Hero */
.tel-hero { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:1rem; margin-bottom:2rem; padding:2rem; background:linear-gradient(135deg,#1a1a2e,#2c3e6e); border-radius:16px; color:#fff; }
.tel-hero-title { margin:0 0 0.3rem; font-size:1.9rem; font-weight:800; }
.tel-hero-sub { margin:0; opacity:0.8; font-size:0.9rem; }
.tel-hero-stats { display:flex; gap:1.2rem; }
.tel-stat { text-align:center; background:rgba(255,255,255,0.12); padding:0.75rem 1.2rem; border-radius:12px; }
.tel-stat-num { display:block; font-size:1.7rem; font-weight:800; }
.tel-stat-label { font-size:0.72rem; opacity:0.8; text-transform:uppercase; letter-spacing:.05em; }

/* Search */
.tel-search-wrap { position:relative; margin-bottom:1.5rem; }
.tel-search-icon { position:absolute; left:1rem; top:50%; transform:translateY(-50%); pointer-events:none; }
.tel-search { width:100%; padding:0.85rem 1rem 0.85rem 2.8rem; border:2px solid #e0e0e0; border-radius:12px; font-size:0.95rem; outline:none; transition:border .2s; box-sizing:border-box; }
.tel-search:focus { border-color:#1a1a2e; }
.tel-clear { position:absolute; right:.8rem; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:#aaa; font-size:1.1rem; }

/* Departments */
.tel-dep-section { margin-bottom: 2.5rem; }
.tel-dep-header { display: flex; align-items: center; gap: 0.8rem; margin-bottom: 1.2rem; padding-bottom: 0.6rem; border-bottom: 2px solid #f0f0f0; }
.tel-dep-icon { font-size: 1.6rem; }
.tel-dep-title { margin: 0; font-size: 1.3rem; font-weight: 700; color: #1a1a2e; flex: 1; }
.tel-dep-count { font-size: 0.8rem; font-weight: 700; color: #1565c0; background: #e3f2fd; padding: 0.3rem 0.8rem; border-radius: 20px; }

/* Grid */
.tel-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1.2rem; }
.tel-card { background:#fff; border-radius:14px; overflow:hidden; box-shadow:0 2px 12px rgba(0,0,0,0.07); cursor:pointer; transition:all 0.25s; border:2px solid transparent; display:flex; align-items:stretch; }
.tel-card:hover { transform:translateY(-4px); box-shadow:0 8px 28px rgba(26,26,46,0.18); border-color:#1a1a2e; }
.tel-card-stripe { width:5px; background:linear-gradient(180deg,#1a1a2e,#2196F3); flex-shrink:0; }
.tel-card-body { flex:1; padding:1.3rem; }
.tel-card-title { margin:0 0 0.6rem; font-size:1rem; font-weight:700; color:#1a1a2e; }
.tel-card-tags { display:flex; flex-wrap:wrap; gap:.4rem; margin-bottom:.5rem; }
.tel-tag { padding:.15rem .55rem; border-radius:20px; font-size:.72rem; font-weight:600; }
.tel-tag-purple { background:#ede7f6; color:#7b1fa2; }
.tel-tag-blue   { background:#e3f2fd; color:#1565c0; }
.tel-card-counts { display:flex; gap:1rem; font-size:0.82rem; font-weight:600; margin-top:0.4rem; }
.tel-count-green { color:#238548; }
.tel-count-blue  { color:#1565c0; }
.tel-count-grey  { color:#aaa; }
.tel-card-cta { display:flex; align-items:center; padding:0 1rem; color:#ccc; font-size:.85rem; font-weight:600; transition:color .2s; }
.tel-card:hover .tel-card-cta { color:#1a1a2e; }

/* Back + Subject Banner */
.tel-back { background:#f0f4f8; border:none; border-radius:8px; padding:.6rem 1.2rem; font-size:.9rem; font-weight:600; color:#444; cursor:pointer; margin-bottom:1.2rem; transition:background .2s; }
.tel-back:hover { background:#e0e7ef; }
.tel-subject-banner { background:linear-gradient(135deg,#1a1a2e,#2c3e6e); color:#fff; border-radius:14px; padding:1.5rem 2rem; margin-bottom:1.5rem; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:1rem; }
.tel-subject-title { margin:0 0 .4rem; font-size:1.4rem; font-weight:700; }
.tel-subject-meta { display:flex; gap:1rem; flex-wrap:wrap; font-size:.85rem; opacity:.8; }
.tel-action-btns { display:flex; gap:.6rem; flex-wrap:wrap; }

/* Buttons */
.tel-btn { padding:.6rem 1.1rem; border:none; border-radius:9px; font-size:.85rem; font-weight:600; cursor:pointer; transition:all .2s; white-space:nowrap; }
.tel-btn-green   { background:#238548; color:#fff; }
.tel-btn-green:hover { background:#1a6b37; }
.tel-btn-blue    { background:#2196F3; color:#fff; }
.tel-btn-blue:hover  { background:#1565c0; }
.tel-btn-yt      { background:#ff0000; color:#fff; }
.tel-btn-yt:hover    { background:#cc0000; }
.tel-btn-outline { background:#f5f5f5; color:#555; }
.tel-btn-outline:hover { background:#e0e0e0; }
.tel-btn:disabled { opacity:.6; cursor:not-allowed; }

/* Detail grid */
.tel-detail-grid { display:flex; flex-direction:column; gap:1.5rem; }
.tel-section { background:#fff; border-radius:14px; padding:1.5rem; box-shadow:0 2px 12px rgba(0,0,0,0.06); display:flex; flex-direction:column; gap:1rem; }
.tel-section-title { margin:0 0 1rem; font-size:1.05rem; font-weight:700; color:#1a1a2e; display:flex; align-items:center; gap:.5rem; }
.tel-count { background:#e8f5e9; color:#238548; padding:.1rem .55rem; border-radius:20px; font-size:.78rem; font-weight:700; }
.tel-empty { text-align:center; padding:1.5rem; color:#aaa; background:#fafafa; border-radius:10px; font-size:.9rem; }

/* Player */
.tel-player-card { border-radius:12px; overflow:hidden; box-shadow:0 4px 20px rgba(0,0,0,0.12); background:#000; }
.tel-player-info { padding:0.8rem 1rem; background:#fff; }
.tel-player-title { margin:0 0 .2rem; font-size:1rem; font-weight:700; color:#1a1a2e; }
.tel-player-desc  { margin:0; font-size:.82rem; color:#888; }
.tel-player-placeholder { background:#f0f4f8; border-radius:12px; padding:3rem 2rem; text-align:center; color:#aaa; font-size:1rem; }

/* Tabs */
.tel-tabs { display:flex; border-bottom:2px solid #eee; }
.tel-tab { background:none; border:none; padding:.7rem 1.2rem; font-size:.9rem; font-weight:600; color:#888; cursor:pointer; border-bottom:3px solid transparent; margin-bottom:-2px; transition:all .2s; }
.tel-tab-active { color:#238548; border-bottom-color:#238548; }

/* Video list */
.tel-video-list { display:flex; flex-direction:column; gap:.4rem; max-height:400px; overflow-y:auto; }
.tel-vitem { border:2px solid transparent; }
.tel-vitem:hover { background:#f5faf7 !important; }
.tel-vitem-active { background:#e8f5e9 !important; border-color:#238548 !important; }
.tel-vitem-icon { font-size:.85rem; color:#888; flex-shrink:0; }

/* Notes list */
.tel-notes-list { display:flex; flex-direction:column; gap:.5rem; }
.tel-thumb { width:80px; height:52px; border-radius:7px; object-fit:cover; flex-shrink:0; }
.tel-thumb-num { width:80px; height:52px; border-radius:7px; background:#e8f5e9; display:flex; align-items:center; justify-content:center; font-weight:700; color:#238548; font-size:1.1rem; flex-shrink:0; }
.tel-note-icon { font-size:1.8rem; flex-shrink:0; }
.tel-vinfo { flex:1; min-width:0; }
.tel-vtitle { font-weight:600; font-size:.9rem; color:#1a1a2e; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tel-vdesc  { font-size:.75rem; color:#aaa; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tel-vdate  { font-size:.72rem; color:#bbb; margin-top:.1rem; }
.tel-row-actions { display:flex; gap:.4rem; align-items:center; }
.tel-icon-btn { width:34px; height:34px; border:none; border-radius:7px; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:0.9rem; font-weight:700; transition:all .15s; text-decoration:none; }
.tel-icon-play { background:#e3f2fd; color:#1565c0; }
.tel-icon-play:hover { background:#bbdefb; }
.tel-icon-dl   { background:#e8f5e9; color:#238548; }
.tel-icon-dl:hover { background:#c8e6c9; }
.tel-icon-del  { background:#ffebee; color:#f44336; }
.tel-icon-del:hover { background:#ffcdd2; }

/* Modal */
.tel-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.55); display:flex; align-items:center; justify-content:center; z-index:9999; padding:1rem; }
.tel-modal { background:#fff; border-radius:16px; width:100%; max-width:520px; box-shadow:0 20px 60px rgba(0,0,0,0.25); animation:telPop .25s ease; }
@keyframes telPop { from { transform:scale(.92); opacity:0; } to { transform:scale(1); opacity:1; } }
.tel-modal-header { display:flex; justify-content:space-between; align-items:center; padding:1.5rem 1.5rem 0; }
.tel-modal-title { margin:0; font-size:1.1rem; font-weight:700; color:#1a1a2e; }
.tel-modal-close { background:none; border:none; cursor:pointer; font-size:1.2rem; color:#aaa; padding:.3rem; }
.tel-modal-close:hover { color:#333; }
.tel-form { padding:1.2rem 1.5rem 1.5rem; display:flex; flex-direction:column; gap:.9rem; }
.tel-label { font-size:.82rem; font-weight:600; color:#555; margin-bottom:-.4rem; }
.tel-input { padding:.75rem 1rem; border:2px solid #e0e0e0; border-radius:9px; font-size:.92rem; outline:none; transition:border .2s; width:100%; box-sizing:border-box; }
.tel-input:focus { border-color:#1a1a2e; }
.tel-textarea { resize:vertical; min-height:60px; }
.tel-file-name { font-size:.8rem; color:#888; padding:.3rem .8rem; background:#f5f5f5; border-radius:6px; }
.tel-yt-preview { width:100%; border-radius:8px; object-fit:cover; max-height:140px; }
.tel-modal-footer { display:flex; gap:.8rem; justify-content:flex-end; padding-top:.4rem; }

/* Spinner */
.tel-spinner-wrap { display:flex; flex-direction:column; align-items:center; gap:.8rem; padding:3rem; color:#888; }
.tel-spinner { width:40px; height:40px; border:4px solid #e0e0e0; border-top-color:#1a1a2e; border-radius:50%; animation:telSpin .8s linear infinite; }
@keyframes telSpin { to { transform:rotate(360deg); } }

/* Empty state */
.tel-empty-state { text-align:center; padding:4rem 2rem; color:#888; }
.tel-empty-state h3 { margin:0 0 .5rem; color:#555; font-size:1.2rem; }

/* ── Mobile Responsive ──────────────────────────────── */
@media (max-width:1024px) {
  .tel-hero { padding:1.2rem; flex-direction:column; align-items:flex-start; border-radius:10px; }
  .tel-hero-title { font-size:1.3rem !important; }
  .tel-hero-sub { font-size:0.82rem; }
  .tel-hero-stats { gap:.7rem; flex-wrap:wrap; }
  .tel-stat { padding:.55rem .9rem; }
  .tel-stat-num { font-size:1.3rem; }

  .tel-grid { grid-template-columns:1fr; gap:.9rem; }
  .tel-card-body { padding:1rem; }

  .tel-subject-banner { padding:1rem 1.2rem; flex-direction:column; align-items:flex-start; border-radius:10px; }
  .tel-subject-title { font-size:1.1rem !important; }
  .tel-action-btns { width:100%; }
  .tel-btn { flex:1; text-align:center; font-size:.8rem; padding:.55rem .7rem; }

  .tel-dep-title { font-size:1.1rem; }

  .tel-video-row, .tel-note-row { padding:.65rem; gap:.7rem; }
  .tel-thumb { width:62px; height:42px; }
  .tel-thumb-num { width:62px; height:42px; font-size:.9rem; }
  .tel-vtitle { font-size:.85rem; }

  .tel-search-wrap { margin-bottom:1rem; }

  .tel-modal { border-radius:12px; }
  .tel-modal-footer { flex-direction:column; }
  .tel-modal-footer .tel-btn { width:100%; justify-content:center; }

  .tel-back { font-size:.82rem; padding:.5rem 1rem; }
}
`;

export default TeacherElearning;
